/**
 * Thin fetch wrapper for the versioned TripBears REST API (`/v1`).
 *
 * Deliberately not axios: the only cross-cutting concerns here are credentials
 * and error shaping, and `fetch` covers both without a dependency. Auth is the
 * better-auth session cookie, so there is no token interceptor to write — the
 * mobile client needs one because it authenticates with a bearer token instead.
 */
import {
  getPasswordSessionGeneration,
  isPasswordSessionTransitionActive,
  waitForPasswordSessionTransition,
} from "@/features/auth/password-session-transition";
import { API_BASE_URL, API_V1_URL } from "@/lib/config";

/** The API's uniform error envelope (src/schemas/shared.schemas.ts). */
type ApiErrorBody = {
  error?: {
    message?: string;
    code?: string;
    details?: unknown;
  };
};

/**
 * A non-2xx response, carrying the status so callers can map it to specific
 * copy. Every invitation failure mode is distinguished by status alone, so the
 * status is the important part — the server message is a fallback for cases we
 * have not enumerated.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string | undefined;
  readonly details: unknown;

  constructor(
    status: number,
    message: string,
    code?: string,
    details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

/** Thrown when the request never reached the API (offline, DNS, CORS). */
export class NetworkError extends Error {
  constructor(cause: unknown) {
    super("Could not reach the TripBears server.");
    this.name = "NetworkError";
    this.cause = cause;
  }
}

async function parseError(response: Response): Promise<ApiError> {
  let message = `Request failed with status ${response.status}.`;
  let code: string | undefined;
  let details: unknown;

  try {
    const body = (await response.json()) as ApiErrorBody;
    if (body.error?.message) message = body.error.message;
    code = body.error?.code;
    details = body.error?.details;
  } catch {
    // A non-JSON error body (a proxy's HTML 502, say) leaves the default
    // message in place. Nothing actionable to recover here.
  }

  return new ApiError(response.status, message, code, details);
}

async function requestApi(
  path: string,
  init: RequestInit,
  baseUrl = API_V1_URL,
) {
  if (isPasswordSessionTransitionActive() && (init.method ?? "GET").toUpperCase() !== "GET") throw new DOMException("Session is being updated. Please try again.", "AbortError");
  await waitForPasswordSessionTransition();
  const generation = getPasswordSessionGeneration();
  let response: Response;
  const headers = new Headers(init.headers);
  // The browser supplies the multipart boundary for file uploads.
  if (!(init.body instanceof FormData) && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  try {
    response = await fetch(`${baseUrl}${path}`, {
      ...init,
      cache: "no-store",
      // Sends and accepts the better-auth session cookie across the
      // app.tripbears.com -> api.tripbears.com origin boundary. Requires the API
      // to allow credentials for this origin, which it does via CORS_ORIGIN.
      credentials: "include",
      headers,
    });
  } catch (cause) {
    throw new NetworkError(cause);
  }

  if (
    response.status === 401 &&
    generation !== getPasswordSessionGeneration()
  ) {
    await waitForPasswordSessionTransition();
    // Retry this stale read once with the replacement cookie. Never replay a
    // write whose outcome could be unknown after a session change.
    if ((init.method ?? "GET").toUpperCase() !== "GET")
      throw new DOMException("Session replaced", "AbortError");
    try {
      response = await fetch(`${baseUrl}${path}`, {
        ...init,
        cache: "no-store",
        credentials: "include",
        headers,
      });
    } catch (cause) {
      throw new NetworkError(cause);
    }
  }
  return response;
}

/** Raw reader pages preserve success and error bodies without domain projections. */
export type RawApiResponse = { status: number; body: unknown };

export async function apiReadRaw(
  path: string,
  signal?: AbortSignal,
): Promise<RawApiResponse> {
  if (!path.startsWith("/v1/") && path !== "/auth/metadata") {
    throw new Error(
      "Raw readers accept versioned API reads or auth metadata only.",
    );
  }
  const response = await requestApi(
    path,
    { method: "GET", signal },
    API_BASE_URL,
  );
  const text = await response.text();
  let body: unknown = text || null;
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      // Preserve a proxy/plain-text response verbatim rather than masking it.
    }
  }
  return { status: response.status, body };
}

export async function apiFetch<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const response = await requestApi(path, init);

  if (!response.ok) {
    throw await parseError(response);
  }

  // 204 and other empty bodies would make response.json() throw.
  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

export function apiPost<T>(path: string, body: unknown): Promise<T> {
  return apiFetch<T>(path, { method: "POST", body: JSON.stringify(body) });
}

export function apiGet<T>(path: string): Promise<T> {
  return apiFetch<T>(path, { method: "GET" });
}
